<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
          </div>
        </section>
      </b-card>

      <b-card no-body>
        <b-row no-gutters>
          <b-col md="4" class="messages-inbox-list-col" v-if="!isMobileViewport || !activeConversation">
            <template v-if="!referenceResults.active">
              <div class="messages-inbox-list-header">
                <span class="messages-inbox-list-title">Conversaciones</span>
                <div class="d-flex" style="gap:6px;">
                  <b-button size="sm"
                            variant="outline-secondary"
                            v-b-tooltip.hover
                            title="Buscar por referencia"
                            @click="toggleReferenceSearch()">
                    <AppIcon :icon="showReferenceSearch ? 'x' : 'search'"></AppIcon>
                  </b-button>
                  <b-button size="sm" variant="outline-primary" @click="toggleStaffPicker()">
                    <AppIcon :icon="showStaffPicker ? 'x' : 'plus'"></AppIcon> Nueva
                  </b-button>
                </div>
              </div>

              <div class="messages-inbox-staff-picker" v-if="showStaffPicker">
                <div style="position:relative">
                  <b-form-input ref="staffSearchInput"
                                v-model="staffSearch.query"
                                size="sm"
                                placeholder="Buscar por nombre..."
                                autocomplete="off"
                                @keyup="onStaffSearchKeyup($event)"
                                @keydown.down.prevent="staffSearchMoveDown"
                                @keydown.up.prevent="staffSearchMoveUp"
                                @keydown.enter.prevent="staffSearchConfirm"
                                @blur="staffSearchBlur"
                                @focus="staffSearchFocus">
                  </b-form-input>

                  <div class="messages-staff-search-results" v-if="staffSearch.show" ref="staffSearchDropdown">
                    <div v-if="staffSearch.loading" class="messages-staff-search-empty text-muted">
                      <b-spinner small class="mr-1"></b-spinner> Buscando...
                    </div>
                    <template v-else>
                      <div v-for="(item, idx) in staffSearch.results"
                           :key="item.id"
                           class="messages-staff-search-item"
                           :class="{ 'messages-staff-search-item--active': idx === staffSearch.activeIdx }"
                           @mousedown.prevent="selectStaffResult(item)">
                        <b-avatar size="1.75rem" :src="item.image" :text="avatarInitial(item.name)" variant="light" class="mr-2"></b-avatar>
                        <span>{{ item.name }}</span>
                      </div>

                      <div v-if="!staffSearch.results.length" class="messages-staff-search-empty text-muted">
                        Sin resultados para "{{ staffSearch.query }}"
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div class="messages-inbox-staff-picker" v-if="showReferenceSearch">
                <div style="position:relative">
                  <b-form-input ref="referenceSearchInput"
                                v-model="referenceSearch.query"
                                size="sm"
                                placeholder="Buscar cliente, proveedor, producto..."
                                autocomplete="off"
                                @keyup="onReferenceSearchKeyup($event)"
                                @keydown.down.prevent="referenceSearchMoveDown"
                                @keydown.up.prevent="referenceSearchMoveUp"
                                @keydown.enter.prevent="referenceSearchConfirm"
                                @blur="referenceSearchBlur"
                                @focus="referenceSearchFocus">
                  </b-form-input>

                  <div class="messages-staff-search-results" v-if="referenceSearch.show" ref="referenceSearchDropdown">
                    <div v-if="referenceSearch.loading" class="messages-staff-search-empty text-muted">
                      <b-spinner small class="mr-1"></b-spinner> Buscando...
                    </div>
                    <template v-else>
                      <div v-for="(item, idx) in referenceSearch.results"
                           :key="item.type + '-' + item.id"
                           class="messages-staff-search-item"
                           :class="{ 'messages-staff-search-item--active': idx === referenceSearch.activeIdx }"
                           @mousedown.prevent="selectReferenceResult(item)">
                        <AppIcon :icon="mentionIcon(item.type)" class="mr-2"></AppIcon>
                        <span class="mr-auto">{{ item.label }}</span>
                        <span class="messages-reference-search-type">{{ mentionLabel(item.type) }}</span>
                      </div>

                      <div v-if="!referenceSearch.results.length && referenceSearch.query.length >= 2" class="messages-staff-search-empty text-muted">
                        Sin resultados para "{{ referenceSearch.query }}"
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div class="messages-inbox-conversations">
                <div v-for="item in conversations"
                     :key="item.id"
                     class="messages-inbox-conversation-item"
                     :class="{ 'messages-inbox-conversation-item--active': activeConversation && activeConversation.id === item.id }"
                     @click="openConversation(item)">
                  <b-avatar size="2.25rem" :src="item.staff_image" :text="avatarInitial(item.staff_name)" variant="light" class="mr-2"></b-avatar>
                  <div class="messages-inbox-conversation-body">
                    <div class="d-flex justify-content-between align-items-baseline">
                      <span class="messages-inbox-conversation-name">{{ item.staff_name }}</span>
                      <span class="messages-inbox-conversation-time" v-if="item.last_message_at">{{ formatDate(item.last_message_at) }}</span>
                    </div>
                    <div class="messages-inbox-conversation-preview">
                      <message-body v-if="item.last_message" :body="item.last_message" plain></message-body>
                      <template v-else>Sin mensajes todavía</template>
                    </div>
                  </div>
                  <b-badge v-if="item.unread_count" variant="danger" pill class="ml-1">{{ item.unread_count > 99 ? '99+' : item.unread_count }}</b-badge>
                  <AppIcon icon="envelope"
                          class="messages-inbox-mark-unread"
                          v-b-tooltip.hover
                          title="Marcar como no leída"
                          @click.stop="markUnread(item)"></AppIcon>
                </div>

                <EmptyState v-if="!loadingConversations && !conversations.length"
                            icon="chat-square-text"
                            title="Todavía no tenés conversaciones"
                            description="Usá el botón Nueva para escribirle a alguien del staff." />
              </div>
            </template>

            <template v-else>
              <div class="messages-inbox-list-header">
                <div class="d-flex align-items-center" style="min-width:0;">
                  <b-button type="button" variant="outline-dark" size="sm" class="mr-2" @click="closeReferenceResults()">
                    <AppIcon icon="arrow-left"></AppIcon>
                  </b-button>
                  <span class="messages-inbox-list-title text-truncate">
                    <AppIcon :icon="mentionIcon(referenceResults.entity.type)" class="mr-1"></AppIcon>
                    {{ referenceResults.entity.label }}
                  </span>
                </div>
              </div>

              <div class="messages-inbox-conversations">
                <div v-if="referenceResults.loading" class="text-center text-muted py-4">
                  <b-spinner small class="mr-2"></b-spinner> Buscando menciones...
                </div>
                <template v-else>
                  <div v-for="row in referenceResults.messages"
                       :key="row.message_id"
                       class="messages-inbox-conversation-item"
                       @click="openReferenceMessage(row)">
                    <b-avatar size="2.25rem" :src="row.other_staff_image" :text="avatarInitial(row.other_staff_name)" variant="light" class="mr-2"></b-avatar>
                    <div class="messages-inbox-conversation-body">
                      <div class="d-flex justify-content-between align-items-baseline">
                        <span class="messages-inbox-conversation-name">{{ row.other_staff_name }}</span>
                        <span class="messages-inbox-conversation-time">{{ formatDate(row.created_at) }}</span>
                      </div>
                      <div class="messages-inbox-conversation-preview">
                        <message-body :body="row.body" plain></message-body>
                      </div>
                    </div>
                  </div>

                  <EmptyState v-if="!referenceResults.messages.length"
                              icon="search"
                              title="Sin menciones"
                              :description="'No se encontraron menciones de ' + referenceResults.entity.label + ' en tus conversaciones.'" />
                </template>
              </div>
            </template>
          </b-col>

          <b-col md="8" class="messages-inbox-thread-col" v-if="!isMobileViewport || activeConversation">
            <template v-if="activeConversation">
              <div class="messages-inbox-thread-header">
                <b-button v-if="isMobileViewport"
                          variant="link"
                          class="messages-inbox-thread-back"
                          v-b-tooltip.hover title="Volver a conversaciones"
                          @click="closeThread()">
                  <AppIcon icon="arrow-left"></AppIcon>
                </b-button>
                <b-avatar size="1.75rem" :src="activeConversation.staff_image" :text="avatarInitial(activeConversation.staff_name)" variant="light" class="mr-2"></b-avatar>
                <strong>{{ activeConversation.staff_name }}</strong>
              </div>

              <div class="messages-inbox-thread-body" ref="threadBody" @scroll="onThreadScroll">
                <div v-if="loadingMessages" class="text-center text-muted py-4">
                  <b-spinner small class="mr-2"></b-spinner> Cargando...
                </div>
                <template v-else>
                  <div v-if="loadingOlderMessages" class="text-center text-muted py-2" style="font-size: 12px;">
                    <b-spinner small class="mr-2"></b-spinner> Cargando mensajes anteriores...
                  </div>
                  <template v-for="item in messagesGrouped">
                    <div v-if="item._type == 'separator'" :key="item._key" class="messages-date-separator">
                      <span>{{ formatMessageDate(item.date) }}</span>
                    </div>
                    <div v-else
                         :key="item._key"
                         class="message-bubble-row"
                         :class="isOwn(item.message) ? 'message-bubble-row--own' : 'message-bubble-row--other'">
                      <div class="message-bubble" :class="isOwn(item.message) ? 'message-bubble--own' : 'message-bubble--other'">
                        <div class="message-bubble-body"><message-body :body="item.message.body"></message-body></div>
                        <div class="message-bubble-time">{{ formatTime(item.message.created_at) }}</div>
                      </div>
                    </div>
                  </template>

                  <div class="text-center text-muted py-4" v-if="!messages.length">
                    Escribí el primer mensaje
                  </div>
                </template>
              </div>

              <div class="messages-inbox-thread-composer">
                <div class="mention-picker" v-if="mentionPicker.active" ref="mentionDropdown">
                  <div v-if="mentionPicker.loading" class="mention-picker-empty text-muted">
                    <b-spinner small class="mr-1"></b-spinner> Buscando...
                  </div>
                  <template v-else-if="mentionPicker.query.length < 2">
                    <div class="mention-picker-empty text-muted">Seguí escribiendo para buscar...</div>
                  </template>
                  <template v-else>
                    <div v-for="(item, idx) in mentionPicker.results"
                         :key="item.type + '-' + item.id"
                         class="mention-picker-item"
                         :class="{ 'mention-picker-item--active': idx === mentionPicker.activeIdx }"
                         @mousedown.prevent="insertMention(item)">
                      <AppIcon :icon="mentionIcon(item.type)" class="mention-picker-item-icon"></AppIcon>
                      <span class="mention-picker-item-label">{{ item.label }}</span>
                      <span class="mention-picker-item-type">{{ mentionLabel(item.type) }}</span>
                    </div>
                    <div v-if="!mentionPicker.results.length" class="mention-picker-empty text-muted">
                      Sin resultados para "{{ mentionPicker.query }}"
                    </div>
                  </template>
                </div>
                <app-textarea ref="composerTextarea"
                                  v-model="newMessageBody"
                                  rows="1"
                                  max-rows="4"
                                  placeholder="Escribir un mensaje..."
                                  :disabled="sending"
                                  @keydown="onComposerKeydown"
                                  @keyup="onComposerKeyup"
                                  @blur="onComposerBlur">
                </app-textarea>
                <b-button variant="primary" :disabled="sending || !newMessageBody || !newMessageBody.trim()" @click="sendMessage()">
                  <b-spinner small v-if="sending"></b-spinner>
                  <AppIcon icon="arrow-up-circle-fill" v-else></AppIcon>
                </b-button>
              </div>
            </template>
            <template v-else>
              <div class="messages-inbox-thread-empty">
                <AppIcon icon="chat-square-text" font-scale="2.5" class="mb-2"></AppIcon>
                <div>Seleccioná una conversación o iniciá una nueva</div>
              </div>
            </template>
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon icon="back"></AppIcon>
            Volver
          </b-button>
        </div>
      </b-card>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Session from '@/handler/session'
  import moment from 'moment'
  import MessageBody from '@/components/inc/messages/MessageBody'
  import { getMentionableConfig } from '@/config/mentionable'
  import { buildMentionToken } from '@/handler/mentions'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  const POLL_INTERVAL_MS = 30000
  const THREAD_POLL_INTERVAL_MS = 7000
  const STAFF_SEARCH_DEBOUNCE_MS = 250
  const MENTION_SEARCH_DEBOUNCE_MS = 250

  export default {
    components: {
      MessageBody,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.MENSAJES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'inbox',
          elements: {}
        },
        isMobileViewport: false,
        loadingConversations: false,
        loadingMessages: false,
        conversations: [],
        activeConversation: null,
        messages: [],
        hasMoreMessages: false,
        loadingOlderMessages: false,
        newMessageBody: '',
        sending: false,
        showStaffPicker: false,
        staffSearch: {
          query: '',
          results: [],
          loading: false,
          show: false,
          activeIdx: -1,
          focusTarget: 'input',
          _timer: null,
        },
        mentionPicker: {
          active: false,
          query: '',
          results: [],
          loading: false,
          activeIdx: -1,
          atIndex: -1,
          _timer: null,
        },
        showReferenceSearch: false,
        referenceSearch: {
          query: '',
          results: [],
          loading: false,
          show: false,
          activeIdx: -1,
          focusTarget: 'input',
          _timer: null,
        },
        referenceResults: {
          active: false,
          entity: null,
          messages: [],
          loading: false,
        },
        pollTimer: null,
        threadPollTimer: null,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadingConversations = true
      this.fetchConversations().then(() => {
        if (this.$route.params.conversationId) {
          var match = this.conversations.find((item) => item.id == this.$route.params.conversationId)
          if (match) this.openConversation(match)
        }
      })
      this.startPolling()
    },
    beforeDestroy() {
      if (this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
      this.stopPolling()
      this.stopThreadPolling()
      clearTimeout(this.mentionPicker._timer)
      clearTimeout(this._mentionBlurTimer)
      clearTimeout(this.referenceSearch._timer)
      clearTimeout(this._referenceBlurTimer)
    },
    computed: {
      // Intercala separadores de fecha entre mensajes de días distintos, para
      // no repetir la fecha en cada burbuja. Se calcula una sola vez por
      // cambio de "messages" (no en cada render de cada burbuja).
      messagesGrouped() {
        var result = []
        var lastDateKey = null
        this.messages.forEach((message) => {
          var dateKey = moment(message.created_at).format('YYYY-MM-DD')
          if (dateKey !== lastDateKey) {
            result.push({ _type: 'separator', _key: 'sep-' + dateKey, date: message.created_at })
            lastDateKey = dateKey
          }
          result.push({ _type: 'message', _key: 'msg-' + message.id, message: message })
        })
        return result
      },
    },
    methods: {
      // RESPONSIVE: en mobile se ve una sola columna a la vez (lista o
      // hilo), como cualquier chat — no las dos apiladas una debajo de la
      // otra obligando a scrollear toda la lista para llegar a la conversación.
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      startPolling() {
        this.pollTimer = setInterval(() => {
          this.fetchConversations()
        }, POLL_INTERVAL_MS)
      },
      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      },
      startThreadPolling() {
        this.stopThreadPolling()
        this.threadPollTimer = setInterval(() => {
          this.pollNewMessages()
        }, THREAD_POLL_INTERVAL_MS)
      },
      stopThreadPolling() {
        if (this.threadPollTimer) {
          clearInterval(this.threadPollTimer)
          this.threadPollTimer = null
        }
      },
      fetchConversations() {
        return serviceAPI.getConversations()
          .then((response) => {
            this.conversations = response.data
            this.loadingConversations = false
          })
          .catch((error) => {
            this.loadingConversations = false
            this.$awn.alert(Error.showError(error))
          })
      },
      markUnread(item) {
        serviceAPI.markUnread(item.id)
          .then(() => {
            this.fetchConversations()
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      toggleStaffPicker() {
        this.showStaffPicker = !this.showStaffPicker
        this.showReferenceSearch = false
        this.resetReferenceSearch()

        if (this.showStaffPicker) {
          this.$nextTick(() => {
            if (this.$refs.staffSearchInput) this.$refs.staffSearchInput.focus()
          })
        } else {
          this.resetStaffSearch()
        }
      },
      resetStaffSearch() {
        clearTimeout(this.staffSearch._timer)
        clearTimeout(this._staffBlurTimer)
        this.staffSearch.query = ''
        this.staffSearch.results = []
        this.staffSearch.show = false
        this.staffSearch.activeIdx = -1
        this.staffSearch.focusTarget = 'input'
      },
      onStaffSearchKeyup(event) {
        // Teclas de navegación/control: no deben disparar una búsqueda nueva
        // (si no, cada flecha resetea la selección y "refresca" el dropdown).
        var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
        if (event && ignoredKeys.indexOf(event.key) !== -1) return

        clearTimeout(this.staffSearch._timer)

        var q = this.staffSearch.query.trim()
        if (!q) {
          this.staffSearch.results = []
          this.staffSearch.show = false
          return
        }

        this.staffSearch._timer = setTimeout(() => this.doStaffSearch(), STAFF_SEARCH_DEBOUNCE_MS)
      },
      doStaffSearch() {
        var q = this.staffSearch.query.trim()
        if (!q) return

        this.staffSearch.loading = true
        this.staffSearch.show = true
        this.staffSearch.activeIdx = -1

        serviceAPI.filterStaffByName({ name: q })
          .then((response) => {
            this.staffSearch.loading = false
            this.staffSearch.results = response.data || []
          })
          .catch(() => {
            this.staffSearch.loading = false
            this.staffSearch.results = []
          })
      },
      staffSearchMoveDown() {
        this.staffSearch.focusTarget = 'dropdown'
        if (this.staffSearch.activeIdx < this.staffSearch.results.length - 1) {
          this.staffSearch.activeIdx++
          this.$nextTick(() => this.scrollActiveStaffResult())
        }
      },
      staffSearchMoveUp() {
        this.staffSearch.focusTarget = 'dropdown'
        if (this.staffSearch.activeIdx > -1) {
          this.staffSearch.activeIdx--
          this.$nextTick(() => this.scrollActiveStaffResult())
        }
      },
      scrollActiveStaffResult() {
        var container = this.$refs.staffSearchDropdown
        if (!container) return
        var active = container.querySelector('.messages-staff-search-item--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      },
      staffSearchConfirm() {
        if (!this.staffSearch.show || this.staffSearch.loading) {
          this.doStaffSearch()
          return
        }

        var idx = this.staffSearch.activeIdx
        if (idx >= 0 && idx < this.staffSearch.results.length) {
          this.selectStaffResult(this.staffSearch.results[idx])
        }
      },
      staffSearchBlur() {
        this._staffBlurTimer = setTimeout(() => { this.staffSearch.show = false }, 200)
      },
      staffSearchFocus() {
        this.staffSearch.focusTarget = 'input'
        if (this.staffSearch.results.length > 0) {
          this.staffSearch.show = true
        }
      },
      selectStaffResult(staff) {
        clearTimeout(this._staffBlurTimer)
        this.startConversation(staff)
      },
      startConversation(staffObject) {
        if (!staffObject) return

        serviceAPI.startConversation(staffObject.id)
          .then((response) => {
            this.showStaffPicker = false
            this.resetStaffSearch()
            this.fetchConversations()
            this.openConversation({
              id: response.data.id,
              staff_id: response.data.staff_id,
              staff_name: response.data.staff_name,
              staff_image: response.data.staff_image,
              unread_count: 0,
            })
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      openConversation(conversation) {
        this.activeConversation = conversation
        this.showStaffPicker = false
        this.resetStaffSearch()
        this.closeMentionPicker()
        this.messages = []
        this.hasMoreMessages = false
        this.loadingOlderMessages = false
        this.loadingMessages = true
        this.fetchMessages()
        this.startThreadPolling()
      },
      // Solo relevante en mobile (ver botón "volver" del header del hilo):
      // en desktop las dos columnas conviven, no hay "volver" que hacer.
      closeThread() {
        this.stopThreadPolling()
        this.activeConversation = null
        this.closeMentionPicker()
        this.messages = []
        this.hasMoreMessages = false
        this.loadingOlderMessages = false
        this.fetchConversations()
      },
      fetchMessages() {
        if (!this.activeConversation) return

        serviceAPI.getMessages(this.activeConversation.id)
          .then((response) => {
            this.messages = (response.data && response.data.data) || []
            this.hasMoreMessages = !!(response.data && response.data.has_more)
            this.loadingMessages = false
            this.$nextTick(() => this.scrollToBottom())
          })
          .catch((error) => {
            this.loadingMessages = false
            this.$awn.alert(Error.showError(error))
          })
      },
      // Polling de mensajes nuevos (7s): pide solo lo posterior al último
      // mensaje cargado y lo agrega al final, sin tocar el historial viejo
      // que el usuario haya cargado scrolleando hacia arriba.
      pollNewMessages() {
        if (!this.activeConversation) return
        if (!this.messages.length) {
          this.fetchMessages()
          return
        }

        var lastId = this.messages[this.messages.length - 1].id

        serviceAPI.getNewMessages(this.activeConversation.id, lastId)
          .then((response) => {
            var incoming = (response.data && response.data.data) || []
            if (!incoming.length) return

            var el = this.$refs.threadBody
            var wasNearBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 80)

            this.messages = this.messages.concat(incoming)

            if (wasNearBottom) {
              this.$nextTick(() => this.scrollToBottom())
            }
          })
          .catch(() => {})
      },
      // Scroll hacia arriba: pide la página anterior al mensaje más viejo
      // cargado y la antepone, preservando la posición visual (si no, el
      // contenido nuevo empuja todo hacia abajo y el usuario pierde el lugar).
      loadOlderMessages() {
        if (!this.activeConversation || !this.hasMoreMessages || this.loadingOlderMessages || !this.messages.length) return

        this.loadingOlderMessages = true
        var oldestId = this.messages[0].id
        var el = this.$refs.threadBody
        var prevScrollHeight = el ? el.scrollHeight : 0
        var prevScrollTop = el ? el.scrollTop : 0

        serviceAPI.getMessages(this.activeConversation.id, oldestId)
          .then((response) => {
            var older = (response.data && response.data.data) || []
            this.hasMoreMessages = !!(response.data && response.data.has_more)
            this.messages = older.concat(this.messages)
            this.loadingOlderMessages = false

            this.$nextTick(() => {
              if (el) {
                el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight)
              }
            })
          })
          .catch(() => {
            this.loadingOlderMessages = false
          })
      },
      onThreadScroll() {
        var el = this.$refs.threadBody
        if (!el) return
        if (el.scrollTop < 60) {
          this.loadOlderMessages()
        }
      },
      sendMessage() {
        if (!this.newMessageBody || !this.newMessageBody.trim()) return
        if (!this.activeConversation) return

        this.closeMentionPicker()
        var body = this.newMessageBody
        this.sending = true

        serviceAPI.sendMessage(this.activeConversation.id, body)
          .then((response) => {
            this.newMessageBody = ''
            this.sending = false
            this.messages.push(response.data)
            this.$nextTick(() => {
              this.scrollToBottom()
              this.focusComposer()
            })
          })
          .catch((error) => {
            this.sending = false
            this.$awn.alert(Error.showError(error))
            this.$nextTick(() => this.focusComposer())
          })
      },
      focusComposer() {
        if (this.$refs.composerTextarea) this.$refs.composerTextarea.focus()
      },
      // Maneja Enter para enviar (como antes, sin Shift/Ctrl/Alt) y, cuando el
      // picker de @ está activo, redirige flechas/Enter/Escape a navegar o
      // confirmar una mención en vez de mover el cursor o mandar el mensaje.
      onComposerKeydown(event) {
        if (this.mentionPicker.active) {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            this.mentionMoveDown()
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            this.mentionMoveUp()
            return
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            this.mentionConfirm()
            return
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            this.closeMentionPicker()
            return
          }
        }

        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault()
          this.sendMessage()
        }
      },
      onComposerKeyup(event) {
        var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
        if (event && ignoredKeys.indexOf(event.key) !== -1) return
        this.detectMentionTrigger(event.target)
      },
      onComposerBlur() {
        this._mentionBlurTimer = setTimeout(() => { this.closeMentionPicker() }, 200)
      },
      // Busca el "@" más cercano hacia atrás desde el cursor: si entre ese "@"
      // y el cursor no hay espacios, es una mención en curso.
      detectMentionTrigger(el) {
        var cursor = el.selectionStart
        var value = el.value
        var textBeforeCursor = value.slice(0, cursor)
        var atIndex = textBeforeCursor.lastIndexOf('@')

        if (atIndex === -1 || /\s/.test(textBeforeCursor.slice(atIndex + 1))) {
          this.closeMentionPicker()
          return
        }

        var fragment = textBeforeCursor.slice(atIndex + 1)
        this.mentionPicker.active = true
        this.mentionPicker.atIndex = atIndex
        this.mentionPicker.query = fragment
        this.mentionPicker.activeIdx = -1

        clearTimeout(this.mentionPicker._timer)
        if (fragment.length < 2) {
          this.mentionPicker.results = []
          this.mentionPicker.loading = false
          return
        }
        this.mentionPicker._timer = setTimeout(() => this.doMentionSearch(), MENTION_SEARCH_DEBOUNCE_MS)
      },
      doMentionSearch() {
        var q = this.mentionPicker.query
        if (!q || q.length < 2) return

        this.mentionPicker.loading = true

        serviceAPI.mentionSearch(q)
          .then((response) => {
            this.mentionPicker.loading = false
            this.mentionPicker.results = response.data || []
          })
          .catch(() => {
            this.mentionPicker.loading = false
            this.mentionPicker.results = []
          })
      },
      mentionMoveDown() {
        if (this.mentionPicker.activeIdx < this.mentionPicker.results.length - 1) {
          this.mentionPicker.activeIdx++
          this.$nextTick(() => this.scrollActiveMentionResult())
        }
      },
      mentionMoveUp() {
        if (this.mentionPicker.activeIdx > -1) {
          this.mentionPicker.activeIdx--
          this.$nextTick(() => this.scrollActiveMentionResult())
        }
      },
      scrollActiveMentionResult() {
        var container = this.$refs.mentionDropdown
        if (!container) return
        var active = container.querySelector('.mention-picker-item--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      },
      mentionConfirm() {
        var idx = this.mentionPicker.activeIdx
        var item = null
        if (idx >= 0 && idx < this.mentionPicker.results.length) {
          item = this.mentionPicker.results[idx]
        } else if (this.mentionPicker.results.length === 1) {
          item = this.mentionPicker.results[0]
        }
        if (!item) return
        this.insertMention(item)
      },
      // Reemplaza desde el "@" hasta el cursor por el token de la mención
      // elegida, y deja el cursor justo después (con un espacio) para seguir
      // escribiendo sin tener que tocar el mouse.
      insertMention(item) {
        clearTimeout(this._mentionBlurTimer)
        var token = buildMentionToken(item.label, item.type, item.id)
        var value = this.newMessageBody
        var atIndex = this.mentionPicker.atIndex
        var el = this.$refs.composerTextarea ? this.$refs.composerTextarea.$refs.textarea : null
        var cursor = el ? el.selectionStart : value.length
        var before = value.slice(0, atIndex)
        var after = value.slice(cursor)
        var newValue = before + token + ' ' + after

        this.newMessageBody = newValue
        this.closeMentionPicker()

        this.$nextTick(() => {
          if (el) {
            var newCursor = (before + token + ' ').length
            el.focus()
            el.setSelectionRange(newCursor, newCursor)
          }
        })
      },
      closeMentionPicker() {
        clearTimeout(this.mentionPicker._timer)
        this.mentionPicker.active = false
        this.mentionPicker.query = ''
        this.mentionPicker.results = []
        this.mentionPicker.loading = false
        this.mentionPicker.activeIdx = -1
        this.mentionPicker.atIndex = -1
      },
      mentionIcon(type) {
        return getMentionableConfig(type).icon
      },
      mentionLabel(type) {
        return getMentionableConfig(type).label
      },
      // Pantalla de "buscar por referencia": mismo buscador @ del composer
      // (un solo input, resultados de todos los tipos mezclados), pero al
      // elegir un resultado no inserta un token — dispara la búsqueda de
      // mensajes que lo mencionan.
      toggleReferenceSearch() {
        this.showReferenceSearch = !this.showReferenceSearch
        this.showStaffPicker = false
        this.resetStaffSearch()

        if (this.showReferenceSearch) {
          this.$nextTick(() => {
            if (this.$refs.referenceSearchInput) this.$refs.referenceSearchInput.focus()
          })
        } else {
          this.resetReferenceSearch()
        }
      },
      resetReferenceSearch() {
        clearTimeout(this.referenceSearch._timer)
        clearTimeout(this._referenceBlurTimer)
        this.referenceSearch.query = ''
        this.referenceSearch.results = []
        this.referenceSearch.show = false
        this.referenceSearch.activeIdx = -1
        this.referenceSearch.focusTarget = 'input'
      },
      onReferenceSearchKeyup(event) {
        var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
        if (event && ignoredKeys.indexOf(event.key) !== -1) return

        clearTimeout(this.referenceSearch._timer)

        var q = this.referenceSearch.query.trim()
        if (q.length < 2) {
          this.referenceSearch.results = []
          this.referenceSearch.show = false
          return
        }

        this.referenceSearch._timer = setTimeout(() => this.doReferenceSearch(), STAFF_SEARCH_DEBOUNCE_MS)
      },
      doReferenceSearch() {
        var q = this.referenceSearch.query.trim()
        if (q.length < 2) return

        this.referenceSearch.loading = true
        this.referenceSearch.show = true
        this.referenceSearch.activeIdx = -1

        serviceAPI.mentionSearch(q)
          .then((response) => {
            this.referenceSearch.loading = false
            this.referenceSearch.results = response.data || []
          })
          .catch(() => {
            this.referenceSearch.loading = false
            this.referenceSearch.results = []
          })
      },
      referenceSearchMoveDown() {
        this.referenceSearch.focusTarget = 'dropdown'
        if (this.referenceSearch.activeIdx < this.referenceSearch.results.length - 1) {
          this.referenceSearch.activeIdx++
          this.$nextTick(() => this.scrollActiveReferenceResult())
        }
      },
      referenceSearchMoveUp() {
        this.referenceSearch.focusTarget = 'dropdown'
        if (this.referenceSearch.activeIdx > -1) {
          this.referenceSearch.activeIdx--
          this.$nextTick(() => this.scrollActiveReferenceResult())
        }
      },
      scrollActiveReferenceResult() {
        var container = this.$refs.referenceSearchDropdown
        if (!container) return
        var active = container.querySelector('.messages-staff-search-item--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      },
      referenceSearchConfirm() {
        if (!this.referenceSearch.show || this.referenceSearch.loading) {
          this.doReferenceSearch()
          return
        }

        var idx = this.referenceSearch.activeIdx
        if (idx >= 0 && idx < this.referenceSearch.results.length) {
          this.selectReferenceResult(this.referenceSearch.results[idx])
        }
      },
      referenceSearchBlur() {
        this._referenceBlurTimer = setTimeout(() => { this.referenceSearch.show = false }, 200)
      },
      referenceSearchFocus() {
        this.referenceSearch.focusTarget = 'input'
        if (this.referenceSearch.results.length > 0) {
          this.referenceSearch.show = true
        }
      },
      selectReferenceResult(item) {
        clearTimeout(this._referenceBlurTimer)
        this.showReferenceSearch = false
        this.resetReferenceSearch()
        this.referenceResults.entity = item
        this.fetchReferenceMessages()
      },
      fetchReferenceMessages() {
        if (!this.referenceResults.entity) return

        this.referenceResults.active = true
        this.referenceResults.loading = true
        this.referenceResults.messages = []

        serviceAPI.searchReferences(this.referenceResults.entity.type, this.referenceResults.entity.id)
          .then((response) => {
            this.referenceResults.loading = false
            this.referenceResults.messages = response.data || []
          })
          .catch((error) => {
            this.referenceResults.loading = false
            this.$awn.alert(Error.showError(error))
          })
      },
      closeReferenceResults() {
        this.referenceResults.active = false
        this.referenceResults.entity = null
        this.referenceResults.messages = []
      },
      openReferenceMessage(row) {
        this.closeReferenceResults()
        this.openConversation({
          id: row.conversation_id,
          staff_id: row.other_staff_id,
          staff_name: row.other_staff_name,
          staff_image: row.other_staff_image,
          unread_count: 0,
        })
      },
      scrollToBottom() {
        var el = this.$refs.threadBody
        if (el) el.scrollTop = el.scrollHeight
      },
      isOwn(message) {
        var currentStaffId = Session.getSession().auth.user.selector_session
        return message.staff_id == currentStaffId
      },
      avatarInitial(name) {
        return name ? name.charAt(0).toUpperCase() : '?'
      },
      formatDate(date) {
        return moment(date).fromNow()
      },
      formatTime(date) {
        return moment(date).format('HH:mm')
      },
      formatMessageDate(date) {
        var m = moment(date)
        var today = moment()
        var yesterday = moment().subtract(1, 'day')
        if (m.isSame(today, 'day')) return 'Hoy'
        if (m.isSame(yesterday, 'day')) return 'Ayer'
        if (m.isSame(today, 'year')) return m.format('D [de] MMMM')
        return m.format('D [de] MMMM [de] YYYY')
      },
    }
  }
</script>
<style scoped>
  .messages-inbox-list-col {
    border-right: 1px solid var(--app-color-border);
    padding: 0;
  }
  .messages-inbox-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid var(--app-color-border);
  }
  .messages-inbox-list-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: var(--app-color-text);
  }
  .messages-inbox-staff-picker {
    padding: 10px 14px;
    border-bottom: 1px solid var(--app-color-border);
    background: var(--app-color-surface-subtle);
  }
  .messages-staff-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: var(--app-shadow-md);
    z-index: 200;
    max-height: 260px;
    overflow-y: auto;
  }
  .messages-staff-search-empty {
    padding: 0.6rem 0.85rem;
    font-size: 0.82rem;
  }
  .messages-staff-search-item {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.85rem;
    cursor: pointer;
    border-bottom: 1px solid var(--app-color-border);
    font-size: 0.85rem;
    transition: background-color 0.1s;
  }
  .messages-staff-search-item:last-child {
    border-bottom: none;
  }
  .messages-staff-search-item:hover,
  .messages-staff-search-item--active {
    background: var(--app-color-primary-soft);
  }
  .messages-reference-search-type {
    flex: 0 0 auto;
    font-size: 0.7rem;
    color: var(--app-color-text-subtle);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .messages-inbox-conversations {
    max-height: 560px;
    overflow-y: auto;
  }
  .messages-inbox-conversation-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 14px;
    border-bottom: 1px solid var(--app-color-border);
    cursor: pointer;
  }
  .messages-inbox-conversation-item:hover {
    background-color: var(--app-color-surface-subtle);
  }
  .messages-inbox-conversation-item--active {
    background-color: var(--app-color-primary-soft);
  }
  .messages-inbox-mark-unread {
    flex: 0 0 auto;
    align-self: center;
    margin-left: 6px;
    font-size: 13px;
    color: var(--app-color-text-subtle);
    opacity: 0;
    transition: opacity 0.15s ease, color 0.15s ease;
  }
  .messages-inbox-conversation-item:hover .messages-inbox-mark-unread {
    opacity: 1;
  }
  .messages-inbox-mark-unread:hover {
    color: var(--app-color-primary);
  }
  .messages-inbox-conversation-body {
    flex: 1;
    min-width: 0;
  }
  .messages-inbox-conversation-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--app-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .messages-inbox-conversation-time {
    flex: 0 0 auto;
    font-size: 10px;
    color: var(--app-color-text-subtle);
  }
  .messages-inbox-conversation-preview {
    font-size: 12px;
    color: var(--app-color-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .messages-inbox-thread-col {
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .messages-inbox-thread-header {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid var(--app-color-border);
  }
  .messages-inbox-thread-back {
    padding: 0;
    margin-right: 8px;
    color: var(--app-color-text-secondary);
  }
  .messages-inbox-thread-body {
    flex: 1;
    min-height: 420px;
    max-height: 560px;
    overflow-y: auto;
    padding: 14px;
    background: var(--app-color-surface-subtle);
  }
  .messages-inbox-thread-composer {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid var(--app-color-border);
  }
  .mention-picker {
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: 100%;
    margin-bottom: 6px;
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    box-shadow: var(--app-shadow-md);
    z-index: 200;
    max-height: 220px;
    overflow-y: auto;
  }
  .mention-picker-empty {
    padding: 0.6rem 0.85rem;
    font-size: 0.82rem;
  }
  .mention-picker-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0.5rem 0.85rem;
    cursor: pointer;
    border-bottom: 1px solid var(--app-color-border);
    font-size: 0.85rem;
    transition: background-color 0.1s;
  }
  .mention-picker-item:last-child {
    border-bottom: none;
  }
  .mention-picker-item:hover,
  .mention-picker-item--active {
    background: var(--app-color-primary-soft);
  }
  .mention-picker-item-icon {
    flex: 0 0 auto;
    opacity: 0.7;
  }
  .mention-picker-item-label {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mention-picker-item-type {
    flex: 0 0 auto;
    font-size: 0.72rem;
    color: var(--app-color-text-subtle);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .messages-inbox-thread-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 480px;
    color: var(--app-color-text-subtle);
  }
</style>
<style>
  .messages-date-separator {
    display: flex;
    justify-content: center;
    margin: 10px 0 8px;
  }
  .messages-date-separator span {
    background: var(--app-color-surface-muted);
    color: var(--app-color-text-muted);
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 10px;
    text-transform: capitalize;
  }
  .message-bubble-row {
    display: flex;
    margin-bottom: 8px;
  }
  .message-bubble-row--own {
    justify-content: flex-end;
  }
  .message-bubble-row--other {
    justify-content: flex-start;
  }
  .message-bubble {
    max-width: 60%;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.35;
  }
  .message-bubble--own {
    background: var(--app-color-primary);
    color: var(--app-color-on-primary);
    border-bottom-right-radius: 3px;
  }
  .message-bubble--other {
    background: var(--app-color-surface-strong);
    color: var(--app-color-text);
    border-bottom-left-radius: 3px;
  }
  .message-bubble-body {
    word-break: break-word;
    white-space: pre-wrap;
  }
  .message-bubble-time {
    margin-top: 3px;
    font-size: 9px;
    opacity: 0.75;
    text-align: right;
  }
</style>
